import React, { FC, useCallback, useEffect, useState } from 'react'
import text from 'styles/utility/text.module.scss'
import { OrderType } from 'types/checkout'
import { Card, CardInner } from 'components/Card'
import { Spacer } from 'components/Utility'
import { FlexRow } from 'components/Flex'
import { BlueButton, TextButton } from 'components/Button'
import { SubDescription } from 'components/Typography'
import { RevenueType } from 'types/revenue'
import { useDispatch, useSelector } from 'react-redux'
import { fetchBasketsFromOrders } from 'reducks/basket/operations'
import { State } from 'index'
import { getBaskets } from 'reducks/basket/selectors'
import { RevenueList } from 'components/List'
import { exportRevenueReportByCsv, exportRevenueReportByXlsx, sumOrdersOriginalPrice } from 'lib/revenue'
import { getStoreRef } from 'reducks/auth/selectors'
import { fetchStoreShippingsFromOrders } from 'reducks/storeShipping/operations'
import { getStoreShippings } from 'reducks/storeShipping/selectors'
import { StoreShippingType } from 'types/storeShipping'
import { BasketType } from 'types/basket'
import { BreadType } from 'types/bread'
import { getBreads } from 'reducks/bread/selectors'

type Props = {
  accountingType: 'order' | 'deliver'
  orderList: OrderType[]
}

const RevenueReportCard: FC<Props> = (props) => {
  const { accountingType, orderList } = props

  const dispatch = useDispatch()
  const baskets: BasketType[] = useSelector((state: State) => getBaskets(state))
  const breads: BreadType[] = useSelector((state: State) => getBreads(state))
  const storeShippings: StoreShippingType[] = useSelector((state: State) => getStoreShippings(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))

  const [revenueList, setRevenueList] = useState<RevenueType[]>([])
  const [expandStatus, setIsExpandedAll] = useState<string>('folded')

  const expandAllAccordion = useCallback(() => setIsExpandedAll('expanded'), [setIsExpandedAll])
  const foldAllAccordion = useCallback(() => setIsExpandedAll('folded'), [setIsExpandedAll])
  const resetExpandedStatus = useCallback(() => setIsExpandedAll(''), [setIsExpandedAll])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBasketsFromOrders(orderList, storeRef))
  }, [orderList, storeRef])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreShippingsFromOrders(orderList, storeRef))
  }, [orderList, storeRef])

  useEffect(() => {
    if (!baskets || !storeShippings) return
    const _revenueList: RevenueType[] = []

    // 当日取り置き・事前予約の売上をまとめる
    for (let i = 0; i < baskets.length; i = (i + 1) | 0) {
      const basket = baskets[i]
      const orders = orderList.filter((order) => order.user_id === basket.user_id && order.date === basket.date)
      for (let j = 0; j < orders.length; j = (j + 1) | 0) {
        const order = orders[j]
        const originalPrice = sumOrdersOriginalPrice([order], true)
        _revenueList.push({
          amount: order.amount,
          basket_number: basket.basket_number,
          checkout_at: order.checkout_at ?? order.updated_at,
          date: order.date,
          delivery_fee_amount: 0,
          discount_amount: order.discount_amount || 0,
          frozen_fee_amount: 0,
          items: order.items,
          originalPrice,
          shopping_bags: order.shopping_bags ?? undefined,
          status: order.status,
          user_id: order.user_id,
        })
      }
    }

    // 取り寄せの売上をまとめる
    for (let i = 0; i < storeShippings.length; i = (i + 1) | 0) {
      const storeShipping = storeShippings[i]
      const orders = orderList.filter((order) => order.checkout_id === storeShipping.store_checkout_ref.id)
      for (let j = 0; j < orders.length; j = (j + 1) | 0) {
        const order = orders[j]
        const originalPrice = sumOrdersOriginalPrice([order], true)
        _revenueList.push({
          amount: order.amount,
          basket_number: storeShipping.shipping_number,
          checkout_at: order.checkout_at ?? order.updated_at,
          date: order.date,
          delivery_fee_amount: order.delivery_fee_amount || 0,
          discount_amount: order.discount_amount || 0,
          frozen_fee_amount: order.frozen_fee_amount || 0,
          items: order.items,
          originalPrice,
          shopping_bags: order.shopping_bags ?? undefined,
          status: order.status,
          user_id: order.user_id,
        })
      }
    }
    // items を bread の order で並び替え
    const nextRevenueList = _revenueList.map((revenue) => {
      revenue.items.sort((a, b) => {
        const aBread = breads.find((bread) => bread.bread_ref?.id === a.bread_ref.id)
        const bBread = breads.find((bread) => bread.bread_ref?.id === b.bread_ref.id)
        // bread の order の昇順で並び替える
        return (aBread?.order || 9999) - (bBread?.order || 9999)
      })
      return revenue
    })
    setRevenueList([...nextRevenueList])
  }, [baskets, storeShippings])

  return (
    <Card>
      <CardInner>
        <p className={`${text.font_xxlarge} ${text.font_bold} ${text.align_center}`}>簡易売上レポート</p>
        <Spacer size={20} />
        <FlexRow justifyContent={'center'}>
          <BlueButton
            label={'CSV書き出し'}
            onClick={() => exportRevenueReportByCsv(revenueList, accountingType)}
            size={'xsmall'}
            type={'main'}
          />
          <Spacer size={8} />
          <BlueButton
            label={'xls書き出し'}
            onClick={() => exportRevenueReportByXlsx(revenueList, accountingType)}
            size={'xsmall'}
            type={'main'}
          />
        </FlexRow>
        <Spacer size={12} />
        <SubDescription description={'※お客さま単位の詳細なレポートは、CSVかxlsファイルをご確認ください。'} />
        {revenueList.length > 0 && (
          <>
            <Spacer size={20} />
            <FlexRow justifyContent={'end'}>
              <TextButton
                disabled={expandStatus === 'expanded'}
                iconPath={expandStatus === 'expanded' ? '/icon/grey/plus.svg' : '/icon/blue/plus.svg'}
                label={'すべて開く'}
                onClick={expandAllAccordion}
              />
              <Spacer size={32} axis={'horizontal'} />
              <TextButton
                disabled={expandStatus === 'folded'}
                iconPath={expandStatus === 'folded' ? '/icon/grey/minus.svg' : '/icon/blue/minus.svg'}
                label={'すべてたたむ'}
                onClick={foldAllAccordion}
              />
            </FlexRow>
          </>
        )}
      </CardInner>
      <Spacer size={12} />
      <RevenueList
        accountingType={accountingType}
        expandStatus={expandStatus}
        resetExpandedStatus={resetExpandedStatus}
        revenueList={revenueList}
      />
    </Card>
  )
}

export default RevenueReportCard
